import { HttpErrorResponse } from "@angular/common/http";
import { AbstractControl, FormControl, FormGroup } from "@angular/forms";


/**
 * utilities
 */
import { AppUtils } from "@core/utilities";
import { IErrorResponse } from "../../utilities/interfaces";

/**
 * Enhanced HTTP error response with convenient error handling methods.
 * Extends Angular's HttpErrorResponse to provide easier access to server errors.
 * 
 * @example
 * ```typescript
 * // Basic usage in error handling
 * catchError((error: HttpErrorResponse) => {
 *   const appError = new AppHttpErrorResponse(error);
 *   console.log(appError.singleErrorMessage);
 *   return throwError(() => appError);
 * })
 * ```
 */
export class AppHttpErrorResponse<TError = unknown> extends HttpErrorResponse {

    /**
     * Gets the complete server error response.
     * 
     * @returns The server error data with message and errors
     * @example
     * ```typescript
     * const error = new AppHttpErrorResponse(httpError);
     * console.log(error.serverError.message); // "Validation failed"
     * console.log(error.serverError.errors);  // { email: ["Invalid email"] }
     * ```
     */
    public get serverError(): IErrorResponse<TError> {
        return this.error as IErrorResponse<TError>;
    }

    /**
     * Gets only the validation errors from server response.
     * 
     * @returns Object containing field-specific error messages
     * @example
     * ```typescript
     * const error = new AppHttpErrorResponse(httpError);
     * const errors = error.clientError;
     * // errors = { email: ["Invalid email"], password: ["Too short"] }
     * ```
     */
    public get clientError(): IErrorResponse<TError>['errors'] {
        return this.serverError.errors;
    }

    /**
     * Creates an enhanced error response wrapper.
     * 
     * @param _response - Original Angular HttpErrorResponse
     * @example
     * ```typescript
     * const appError = new AppHttpErrorResponse(httpErrorResponse);
     * ```
     */
    constructor(private readonly _response: HttpErrorResponse) {
        super({
            error: _response.error,
            headers: _response.headers,
            status: _response.status,
            statusText: _response.statusText,
            url: _response.url === null ? undefined : _response.url
        });
    }
    
    /**
     * Gets a formatted error message ready for display.
     * Converts arrays to HTML line breaks, returns strings as-is.
     * 
     * @returns Formatted error message or null
     * @example
     * ```typescript
     * const error = new AppHttpErrorResponse(httpError);
     * const message = error.singleErrorMessage;
     * // For array: ["Error 1", "Error 2"] → "Error 1<br />Error 2"
     * // For string: "Simple error" → "Simple error"
     * ```
     */
    public get singleErrorMessage(): string | null {
        
        // ES6 Destructuring
        // get errors
        const { errors } = this.serverError;
        
        // check if errors is an array
        if (Array.isArray(errors)) {
            return errors.toString().replaceAll(',', '<br />');
        }
        else if (errors instanceof Object) {
            return AppUtils.chainReducerFn<IErrorResponse<any>['errors'], string>(errors);
        }
        // check if errors is a string
        else if (typeof errors === 'string') {
            return errors;
        }
        
        return null;
    }

    /**
     * @beta Feature
     * @module BetaFeature
     * @experimental
     * NOTE: This entire feature is under active development and may change.
     * @deprecated
     */
    private _handleClientError(error: HttpErrorResponse): any {

        // ES6 Destructuring
        // get status
        const { status } = error;

        switch(status) {
            case 401:
                return this._handle401Error(error);
            case 403:
                return this._handle403Error(error);
            case 404:
                return this._handle404Error(error);
            case 500:
                return this._handle500Error(error);
            default:
                return error.error;
        }

    }    

    /**
     * 
     * @beta Feature
     * @module BetaFeature
     * @experimental
     * NOTE: This entire feature is under active development and may change.
     * @deprecated
     */
    private _handle401Error(error: HttpErrorResponse): any {
        return error.error;
    }

    /**
     * @beta Feature
     * @module BetaFeature
     * @experimental
     * NOTE: This entire feature is under active development and may change.
     * @deprecated
     */
    private _handle403Error(error: HttpErrorResponse): any {
        return error.error;
    }

    /**
     * @beta Feature
     * @module BetaFeature
     * @experimental
     * NOTE: This entire feature is under active development and may change.
     * @deprecated
     */
    private _handle404Error(error: HttpErrorResponse): any {
        return error.error;
    }

    /**
     * @beta Feature
     * @module BetaFeature
     * @experimental
     * NOTE: This entire feature is under active development and may change.
     * @deprecated
     */
    private _handle500Error(error: HttpErrorResponse): any {
        return error.error;
    }

    /**
     * Handles the server side validation errors for a form control.
     * This method will set the server side validation errors to the form control.
     * @param control - The form control to handle the server side validation errors
     * @example
     * ```typescript
     * const error = new AppHttpErrorResponse(httpError);
     * error.handleControlServerSideValidationErrors(formControl);
     * ```
     */
    public handleControlServerSideValidationErrors(control: AbstractControl): void {

        // void validation
        if (control == null) return;

        // check if the status is not 422
        if (this.status !== 422) return;

        // ES6 Destructuring
        const errors = this.clientError as IErrorResponse<{ [key: string]: string[] }>['errors'];

        // check if the errors is empty
        if (Object.keys(errors ?? {}).length === 0) return;

        // iterate over the errors and set them to the form controls
        Object.keys(errors)
                .forEach(key => {
                    
                    // init
                    let _getControl: AbstractControl | null = null;;

                    // get error by control name
                    const serverError = errors[key][0];
                    
                    // set control ref
                    if (control instanceof FormGroup) {
                        _getControl = control.get(key);

                    } else if (control instanceof FormControl) {
                        _getControl = control
                    }

                    // set error
                    if (_getControl != null) {
                        _getControl?.setErrors({ serverError });
                        _getControl.markAsTouched();
                    }
                });

    }

}
